// Claude-Artifacts-style side panel: renders the `update_artifact` tool's
// current HTML revision in a sandboxed iframe, with revision history nav,
// download, and close. Content is agent-authored — `sandbox="allow-scripts"`
// WITHOUT `allow-same-origin` is the entire security boundary here (letting
// scripts run while denying same-origin access to the parent document); do
// not add `allow-same-origin` back, that combination lets sandboxed script
// escape the sandbox in some browsers.
import { Download, History, X } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { useArtifactPanelStore, useThreadArtifactPanel } from '@/shared/stores/artifactPanel';

function downloadHtml(title: string, html: string) {
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${title || 'artifact'}.html`;
  anchor.click();
  URL.revokeObjectURL(url);
}

// `threadId` scopes this panel to one thread's revision history (see
// shared/stores/artifactPanel/store.ts) — pass the same key ChatWorkspace
// uses (`threadId ?? 'new'`) so switching threads never shows another
// thread's artifacts.
export function ArtifactPanel({ threadId }: { threadId: string }) {
  const { isOpen, title, html, revisions, currentRevisionIndex } = useThreadArtifactPanel(threadId);
  const viewRevision = useArtifactPanelStore((s) => s.viewRevision);
  const close = useArtifactPanelStore((s) => s.close);

  if (!isOpen) return null;

  return (
    <aside className="flex h-full min-w-0 flex-col border-l border-border/60 bg-background">
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
        <span className="truncate text-sm font-medium text-foreground">{title || 'Artifact'}</span>
        <div className="flex items-center gap-1">
          {revisions.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Revision history">
                  <History />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {revisions.map((revision, index) => (
                  <DropdownMenuItem
                    key={revision.timestamp}
                    onSelect={() => viewRevision(threadId, index)}
                    aria-current={index === currentRevisionIndex}
                  >
                    {revision.title || `Revision ${index + 1}`}
                    {index === currentRevisionIndex ? ' (current)' : ''}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Download artifact"
            onClick={() => downloadHtml(title, html)}
            disabled={!html}
          >
            <Download />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Close artifact panel"
            onClick={() => close(threadId)}
          >
            <X />
          </Button>
        </div>
      </div>
      {revisions.length > 0 ? (
        <iframe
          title={title || 'Artifact preview'}
          srcDoc={html}
          sandbox="allow-scripts"
          className="h-full w-full flex-1 border-0 bg-white"
        />
      ) : (
        // Reached via the on-demand reopen button when the store has no
        // revisions for this thread yet (e.g. a fresh reload — see
        // shared/stores/artifactPanel/store.ts's persistence note). Never
        // render a blank iframe in this state.
        <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-muted-foreground">
          No artifact yet for this conversation.
        </div>
      )}
    </aside>
  );
}
